import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Car, CalendarDays, FileText, Users, UserCheck, CreditCard, Clock, Wrench, FolderOpen, BarChart3,
  Settings, LogOut, Search, Bell, Menu, X, Building2, LifeBuoy, Receipt,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fetchBlobUrl, label } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared";
import ThemeToggle, { applyMode } from "@/components/ThemeToggle";
import { LayoutOverrides } from "@/components/LayoutOverrides";
import { InternalEditor } from "@/components/InternalEditor";

const NAV = [
  ["/", "Dashboard", LayoutDashboard], ["/veicoli", "Veicoli", Car], ["/calendario", "Calendario", CalendarDays],
  ["/noleggi", "Tutti i noleggi", FileText], ["/clienti", "Clienti", Users], ["/dipendenti", "Dipendenti", UserCheck],
  ["/pagamenti", "Pagamenti", CreditCard], ["/scadenze", "Scadenze", Clock], ["/manutenzioni", "Manutenzioni", Wrench], ["/assistenza", "Assistenza", LifeBuoy],
];
const PLATFORM_NAV = [["/piattaforma", "Società", Building2], ["/fatture", "Fatturazione", Receipt], ["/impostazioni", "Impostazioni", Settings]];

export function Brand({ company, compact, big, platform }) {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let url;
    if (company?.logo_id && !platform) fetchBlobUrl("/settings/logo").then((u) => { url = u; setLogo(u); }).catch(() => setLogo(null));
    else setLogo(null);
    return () => url && URL.revokeObjectURL(url);
  }, [company?.logo_id, company?.id, platform]);
  if (platform) return (
    <div className="flex items-center gap-3" data-testid="brand-nolium">
      <div className="bg-white rounded-lg p-1.5"><img src="/nolium-mark.png" alt="NOLIUM" className="h-9 w-auto" /></div>
      <div><div className="font-display font-extrabold text-white tracking-wider text-lg">NOLIUM</div>
        <div className="text-[10px] text-[#A6CE39]">L'ecosistema digitale del noleggio</div></div>
    </div>
  );
  return (
    <div className={`flex items-center gap-3 min-w-0 ${big ? "flex-col justify-center w-full" : ""}`} data-testid="brand-block">
      {logo ? <img src={logo} alt="logo" className={big ? "h-24 w-auto max-w-[200px] object-contain" : "h-10 w-auto max-w-[120px] object-contain rounded"} /> :
        <div className="h-10 w-10 rounded-lg bg-brand grid place-items-center font-display font-bold text-white">{(company?.name || "G")[0]}</div>}
      {!compact && !(big && logo) && <div className="min-w-0"><div className="font-display font-bold text-white truncate">{company?.name || "Gestionale"}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Noleggio auto</div></div>}
    </div>
  );
}

function TrialDays({ company }) {
  const b = company?.billing;
  if (b?.status !== "prova" || !b.trial_until) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(`${String(b.trial_until).slice(0, 10)}T00:00:00`);
  const days = Math.max(0, Math.round((end - today) / 86400000));
  const txt = days === 0 ? "Prova gratuita: ultimo giorno" : `Prova gratuita: ${days} ${days === 1 ? "giorno rimanente" : "giorni rimanenti"}`;
  return <div className={`mt-3 text-xs font-semibold px-3 py-1 rounded-full border text-center ${days <= 2 ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-brand/40 text-brand bg-brand/10"}`} data-testid="trial-days-left">{txt}</div>;
}

function Sidebar({ open, onClose, onLogout, company, platform }) {
  const { state, can } = useAuth();
  const items = platform ? PLATFORM_NAV : [...NAV.slice(0, -1), ...(state.features?.einvoice && can("invoices", "view") ? [["/fatture", "Fatture", Receipt]] : []), NAV[NAV.length - 1]];
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={onClose} />}
      <aside className={`fixed z-40 inset-y-0 left-0 w-64 border-r border-zinc-800 flex flex-col transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "var(--sidebar)" }} data-testid="sidebar">
        <div className="relative px-5 py-6 flex flex-col items-center justify-center border-b border-zinc-800/80">
          <NavLink to={platform ? "/piattaforma" : "/"} onClick={onClose} aria-label="Torna alla dashboard" data-testid="brand-home-link"><Brand company={company} big platform={platform} /></NavLink>
          {!platform && <TrialDays company={company} />}
          <button className="lg:hidden absolute right-4 top-4 text-zinc-400" onClick={onClose} data-testid="sidebar-close"><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto scroll-thin py-4 px-3 space-y-0.5">
          {items.map(([to, text, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"} onClick={onClose} data-testid={`nav-${text.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "nav-active" : "text-zinc-300 hover:text-white hover:bg-zinc-900"}`}>
              <Icon size={18} /> {text}
            </NavLink>
          ))}
          <button onClick={onLogout} data-testid="nav-esci" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-zinc-900">
            <LogOut size={18} /> Esci
          </button>
        </nav>
        <div className="p-4 text-[11px] text-zinc-500 border-t border-zinc-800 flex items-center gap-2" data-testid="sidebar-powered">
          <img src="/nolium-mark.png" alt="" className="h-5 w-auto" /><span><b className="text-zinc-300 tracking-wider">NOLIUM</b> · {platform ? "Piattaforma" : `Piano ${label(company?.plan)?.toUpperCase?.()}`}</span></div>
      </aside>
    </>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative flex-1 max-w-md">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, targa, contratto…" data-testid="global-search-input"
        className="w-full h-10 pl-9 pr-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-brand" />
      {res.length > 0 && (
        <div className="absolute mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md shadow-2xl z-50 overflow-hidden" data-testid="global-search-results">
          {res.map((r, i) => (
            <button key={i} onClick={() => { nav(r.link); setQ(""); }} className="w-full text-left px-3 py-2 hover:bg-zinc-800 flex justify-between gap-2 text-sm">
              <span className="truncate">{r.label}<span className="text-zinc-500 ml-2 text-xs">{r.sub}</span></span>
              <span className="text-[10px] uppercase text-zinc-500">{r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-10 w-10 grid place-items-center rounded-md hover:bg-zinc-900" data-testid="notifications-button">
          <Bell size={18} />
          {items.length > 0 && <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-[10px] grid place-items-center text-white">{items.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-zinc-800 font-display font-semibold">Notifiche</div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="p-4 text-sm text-zinc-500">Nessuna notifica</div>}
          {items.map((n, i) => (
            <button key={i} onClick={() => nav(n.link)} className="w-full text-left px-4 py-3 text-sm hover:bg-zinc-800 border-b border-zinc-800/60 flex gap-2">
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.level === "error" ? "bg-red-500" : "bg-amber-500"}`} />{n.text}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CompanySwitcher() {
  const { state } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/companies").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;
  const go = (v) => { if (v === "nolium") localStorage.removeItem("company_id"); else localStorage.setItem("company_id", v); window.location.href = v === "nolium" ? "/piattaforma" : "/"; };
  return (
    <Select value={state.platform ? "nolium" : state.company?.id} onValueChange={go}>
      <SelectTrigger className="w-28 sm:w-48 h-10" data-testid="company-switcher"><Building2 size={14} className="mr-1 shrink-0" /><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="nolium" data-testid="switch-nolium">NOLIUM · Piattaforma</SelectItem>
        {list.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`switch-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Layout() {
  const { state, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);
  const nav = useNavigate();
  const u = state.user;
  const forced = !state.platform && !!state.company?.force_light;
  useEffect(() => { applyMode(forced ? "light" : localStorage.getItem("ui_theme") || "dark"); }, [forced]);
  return (
    <div className="min-h-screen grain">
      {!state.platform && <LayoutOverrides key={state.company?.id} />}
      <Sidebar open={open} onClose={() => setOpen(false)} onLogout={() => setConfirmOut(true)} company={state.company} platform={state.platform} />
      <div className="lg:pl-64 relative z-10">
        <header className="sticky top-0 z-20 h-16 px-4 md:px-8 flex items-center gap-3 border-b border-zinc-800 bg-black/85 backdrop-blur-md">
          <button className="lg:hidden h-10 w-10 grid place-items-center" onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu size={20} /></button>
          {!state.platform && <GlobalSearch />}
          <div className="ml-auto flex items-center gap-1 sm:gap-2 min-w-0">
            {u.role === "superadmin" && <CompanySwitcher />}
            {u.role === "superadmin" && !state.platform && state.company?.id && <InternalEditor cid={state.company.id} />}
            {!forced && <ThemeToggle />}
            {!state.platform && <Notifications />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 h-10 px-2 rounded-md hover:bg-zinc-900" data-testid="profile-menu">
                  <div className="h-8 w-8 rounded-full bg-zinc-800 grid place-items-center text-sm font-semibold">{(u.name || u.email)[0].toUpperCase()}</div>
                  <div className="hidden md:block text-left leading-tight"><div className="text-sm">{u.name}</div><div className="text-[11px] text-zinc-500">{label(u.role)}</div></div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-zinc-400">{u.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/impostazioni")} data-testid="profile-settings">Impostazioni</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setConfirmOut(true)} data-testid="profile-logout">Esci</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-[1600px]"><Outlet /></main>
      </div>
      <ConfirmDialog open={confirmOut} onOpenChange={setConfirmOut} title="Uscire dal gestionale?" description="La sessione verrà chiusa."
        confirmText="Esci" onConfirm={async () => { await logout(); nav("/login"); }} />
    </div>
  );
}
